import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { Observable, of, tap } from 'rxjs';

import { UriConfig } from '@app/app.config';
import { APP_ROUTES } from '@app/app.routes.config';
import { UserLoginDetailsModel } from '@app/common/models/UserLoginDetailsModel';
import { SentryUserModel } from '@models/SentryUserModel';
import { SnackBarService } from '@services/snack-bar.service';
import { UserRole } from 'src/app/common/Enums/UserRoleEnum';

import { LocalStorageService } from './local-storage.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  protected http = inject(HttpClient);
  private uriConfig = inject(UriConfig);
  private localStorageService = inject(LocalStorageService);
  private snackBarService = inject(SnackBarService);

  httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' })
  };

  currentRole: UserRole;

  isLoggingOut = signal<boolean>(false);

  private readonly logoutChannel = new BroadcastChannel('auth:logout');

  private readonly loginChannel = new BroadcastChannel('auth:login');

  constructor() {
    this.logoutChannel.onmessage = () => {
      if (!window.location.pathname.startsWith('/' + APP_ROUTES.AUTH)) {
        this.isLoggingOut.set(true);
        window.location.href = '/' + APP_ROUTES.AUTH + '/login';
      }
    };

    this.loginChannel.onmessage = () => {
      if (window.location.pathname.startsWith('/' + APP_ROUTES.AUTH)) {
        window.location.reload();
      }
    };
  }

  broadcastLogout(): void {
    this.logoutChannel.postMessage('logout');
  }

  broadcastLogin(): void {
    this.loginChannel.postMessage('login');
  }

  logOut(): void {
    this.isLoggingOut.set(true);
    this.http.delete(this.uriConfig.logout, this.httpOptions).subscribe(() => {
      this.broadcastLogout();
      this.localStorageService.logOut();
      window.location.href = '/' + APP_ROUTES.AUTH + '/login';
    });
  }

  logOutB2C(playerId: number): Observable<void> {
    return this.http.delete<void>(this.uriConfig.logout + '/' + playerId).pipe(
      tap(() => {
        this.snackBarService.showSnackBar(`The user with ID:${playerId} was logout successfully`);
      })
    );
  }

  refreshToken(options: { headers: HttpHeaders }): Observable<{ accessToken: string; refreshToken: string }> {
    return this.http.post<{ accessToken: string; refreshToken: string }>(this.uriConfig.refresh, {}, options);
  }

  getCurrentUser(): Observable<string> {
    return of(this.localStorageService.getToken());
  }

  login(user: UserLoginDetailsModel): Observable<{ accessToken: string; refreshToken: string }> {
    return this.http.post<{ accessToken: string; refreshToken: string }>(this.uriConfig.login, user, this.httpOptions);
  }

  validateRestoreToken(body): Observable<{ isValid: boolean }> {
    return this.http.post<{ isValid: boolean }>(this.uriConfig.validateResetToken, body);
  }

  resetPassword(body): Observable<string> {
    return this.http.post<string>(this.uriConfig.resetPassword, body);
  }

  restorePassword(body): Observable<string> {
    return this.http.post<string>(this.uriConfig.restorePassword, body);
  }

  changePassword(body): Observable<string> {
    return this.http.post<string>(this.uriConfig.changePassword, body);
  }

  getUserToken() {
    return this.http.post<{ accessToken: string; refreshToken: string }>(this.uriConfig.auth + '/preview-user', {});
  }

  getAccessAndRefreshTokensForB2C(userId: number) {
    return this.http.post<{ accessToken: string; refreshToken: string }>(
      this.uriConfig.auth + `/signin/${userId}/b2c`,
      {}
    );
  }

  setSentryUser(user: SentryUserModel): void {
    const { email } = user;
    Sentry.setUser({ email });
  }

  setCurrentRole(role): void {
    this.currentRole = role;
  }

  getCurrentRole(): UserRole {
    return this.currentRole;
  }

  isAdmin(): boolean {
    return this.currentRole === UserRole.ADMIN;
  }
}
